import { t } from "@lingui/macro";
import {
  Camera,
  Gear,
  Trash,
  UploadSimple,
  User,
} from "@phosphor-icons/react";
import { defaultResumeData } from "@stylingcv/schema";
import {
  Avatar,
  AvatarImage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import imageCompression from "browser-image-compression";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { SignupModal } from "@/client/components/auth/signup-modal";
import { toast } from "@/client/hooks/use-toast";
import { useUploadImage } from "@/client/services/storage";
import { useAuthStore } from "@/client/stores/auth";
import { useResumeStore } from "@/client/stores/resume";

import { PictureOptions } from "./options";

export const PictureSection = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, loading: isUploading } = useUploadImage();
  const isLoggedIn = useAuthStore((state) => !!state.user);

  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const setValue = useResumeStore((state) => state.setValue);
  const picture = useResumeStore((state) => {
    try {
      return state.resume.data.basics.picture;
    } catch {
      return defaultResumeData.basics.picture;
    }
  });

  const isValidUrl = useMemo(() => z.string().url().safeParse(picture.url).success, [picture.url]);

  const processAndUpload = useCallback(
    async (file: File) => {
      if (!isLoggedIn) {
        toast({
          variant: "error",
          title: t`Authentication Required`,
          description: t`Please sign up or log in to upload profile pictures. You can still add images using external URLs.`,
        });
        return;
      }

      const acceptedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!acceptedTypes.includes(file.type)) {
        toast({
          variant: "error",
          title: t`Invalid File`,
          description: t`Please select an image file (JPEG, PNG, GIF, or WebP).`,
        });
        return;
      }

      setIsProcessing(true);
      setUploadProgress(10);

      try {
        let processedFile = file;
        setUploadProgress(30);

        const maxSizeMB = 1;
        if (file.size > maxSizeMB * 1024 * 1024) {
          processedFile = await imageCompression(file, {
            maxSizeMB,
            maxWidthOrHeight: 800,
            useWebWorker: true,
            onProgress: (progress: number) => {
              setUploadProgress(30 + progress * 0.4);
            },
          });
        }

        setUploadProgress(70);

        const response = await uploadImage(processedFile);
        setValue("basics.picture.url", response.data);

        setUploadProgress(100);
        toast({
          variant: "default",
          title: t`Image Uploaded`,
          description: t`Your profile picture has been uploaded successfully.`,
        });

        setTimeout(() => {
          setIsProcessing(false);
          setUploadProgress(0);
        }, 400);
      } catch {
        toast({
          variant: "error",
          title: t`Upload Failed`,
          description: t`Failed to upload image. Please try again or use an external URL instead.`,
        });
        setIsProcessing(false);
        setUploadProgress(0);
      }

      if (inputRef.current) inputRef.current.value = "";
    },
    [isLoggedIn, uploadImage, setValue],
  );

  const onFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) processAndUpload(file);
    },
    [processAndUpload],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files?.[0];
      if (file) processAndUpload(file);
    },
    [processAndUpload],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const onRemoveImage = () => {
    setValue("basics.picture.url", "");
  };

  return (
    <>
      <div>
        <input
          ref={inputRef}
          hidden
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          disabled={isProcessing || isUploading}
          onChange={onFileChange}
        />

        <AnimatePresence mode="wait">
          {isValidUrl ? (
            /* ── Has Image: show avatar + actions ── */
            <motion.div
              key="preview"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-4"
            >
              <div className="relative shrink-0 group">
                <Avatar className="size-[72px] rounded-full border-2 border-[var(--accent,#268599)]/30">
                  <AvatarImage src={picture.url} className="object-cover" />
                </Avatar>
                {/* Hover overlay to replace */}
                <button
                  type="button"
                  disabled={isProcessing || !isLoggedIn}
                  onClick={() => inputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 cursor-pointer"
                >
                  <Camera size={20} weight="fill" className="text-white" />
                </button>
              </div>

              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <span className="text-xs text-[var(--fg-3,#737373)] truncate">{t`Photo uploaded`}</span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={onRemoveImage}
                    className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-medium text-red-500 transition-colors hover:bg-red-100"
                  >
                    <Trash size={13} weight="bold" />
                    {t`Remove`}
                  </button>

                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-lg border border-[var(--border,#e5e5e5)] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[var(--fg-2,#404040)] transition-colors hover:bg-gray-50"
                      >
                        <Gear size={13} weight="bold" />
                        {t`Style`}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 border-[var(--border,#e5e5e5)] bg-white p-3">
                      <PictureOptions />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </motion.div>
          ) : (
            /* ── No Image ── */
            <motion.div
              key="dropzone"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              {isProcessing ? (
                /* ── Processing state ── */
                <div className="flex flex-col items-center gap-3 rounded-xl border border-[var(--accent,#268599)]/20 bg-[var(--accent,#268599)]/5 p-5">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  >
                    <UploadSimple size={28} weight="duotone" className="text-[var(--accent,#268599)]" />
                  </motion.div>
                  <div className="w-full max-w-[200px]">
                    <div className="relative h-1.5 overflow-hidden rounded-full bg-gray-200">
                      <motion.div
                        className="absolute inset-y-0 left-0 rounded-full bg-[var(--accent,#268599)]"
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <p className="mt-1.5 text-center text-[10px] text-[var(--fg-4,#a3a3a3)]">
                      {uploadProgress < 30 && t`Validating...`}
                      {uploadProgress >= 30 && uploadProgress < 70 && t`Compressing...`}
                      {uploadProgress >= 70 && uploadProgress < 100 && t`Uploading...`}
                      {uploadProgress >= 100 && t`Done!`}
                    </p>
                  </div>
                </div>
              ) : (
                /* ── No-image layout: horizontal row ── */
                <div className="flex items-center gap-4">
                  {/* Avatar placeholder — drag-drop target */}
                  <div
                    className="relative size-[72px] shrink-0"
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                  >
                    <div
                      className={cn(
                        "size-full rounded-full border-2 border-dashed flex items-center justify-center transition-colors",
                        isDragging
                          ? "border-[var(--accent,#268599)] bg-[var(--accent,#268599)]/5"
                          : "border-gray-300 bg-gray-50",
                      )}
                    >
                      <User
                        size={28}
                        className={cn(
                          "transition-colors",
                          isDragging ? "text-[var(--accent,#268599)]" : "text-gray-300",
                        )}
                      />
                    </div>
                  </div>

                  {/* Upload actions */}
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (isLoggedIn) {
                            inputRef.current?.click();
                          } else {
                            setShowSignupModal(true);
                          }
                        }}
                        className="flex items-center gap-1.5 rounded-lg border border-[var(--border,#e5e5e5)] bg-white px-3 py-1.5 text-[13px] font-medium text-[var(--fg-2,#404040)] transition-colors hover:bg-gray-50"
                      >
                        <UploadSimple size={14} />
                        {t`Upload`}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowUrlInput(!showUrlInput)}
                        className="text-[13px] text-[var(--fg-3,#737373)] transition-colors hover:text-[var(--fg-1,#171717)]"
                      >
                        {t`From URL`}
                      </button>
                      <span className="text-[11px] text-[var(--fg-4,#a3a3a3)]">
                        PNG/JPG · 1:1, max 4 MB
                      </span>
                    </div>

                    {showUrlInput && (
                      <Input
                        id="basics.picture.url"
                        placeholder="https://..."
                        value={picture.url}
                        className="border-[var(--border,#e5e5e5)] bg-white text-[var(--fg-1,#171717)] placeholder:text-[var(--fg-4,#a3a3a3)]"
                        onChange={(event) => {
                          setValue("basics.picture.url", event.target.value);
                        }}
                      />
                    )}
                  </div>

                  {/* Settings button (teal) */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent,#268599)] text-white transition-colors hover:bg-[var(--accent,#268599)]/90"
                      >
                        <Gear size={14} weight="bold" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 border-[var(--border,#e5e5e5)] bg-white p-3">
                      <PictureOptions />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        transferGuestResume
      />
    </>
  );
};
